<template>
  <div class="row">
    <div class="title small">{{ $t('Identity Verification') }}</div>
    
    <!-- Active Session Display -->
    <div v-if="hasActiveSession">
      <!-- Success message -->
      <div v-if="showSuccessMessage" class="desc green" style="margin-top: 16px;">
        {{ $t('Verification session started successfully!') }}
      </div>
      
      <!-- Status display -->
      <div style="margin-top: 16px;">
        <div class="desc" style="margin-bottom: 8px;">{{ $t('Status') }}:</div>
        <div class="status-badge" :class="sessionStatus">
          {{ formatStatus(sessionStatus) }}
        </div>
      </div>

      <!-- Pending State -->
      <div v-if="isPendingVerification">
        <div class="desc" style="margin-top: 16px;">
          {{ $t('Please complete verification in the opened tab. We will automatically detect when you are done.') }}
        </div>
        
        <div style="margin: 24px auto 0; display: flex; gap: 16px; flex-wrap: wrap; max-width: 596px; justify-content: center;">
          <button class="button" style="flex: 1 1 280px; max-width: 290px;" @click="reopenVerification">
            <span>{{ $t('Reopen Tab') }}</span>
          </button>
          
          <button class="button gray" style="flex: 1 1 280px; max-width: 290px;" @click="cancelSession">
            <span>{{ $t('Cancel') }}</span>
          </button>
        </div>
      </div>

      <!-- Success State -->
      <div v-else-if="sessionStatus === 'success'">
        <div class="desc green" style="margin-top: 16px; text-align: center;">
          {{ $t('Verification completed successfully!') }}
        </div>
      </div>

      <!-- Failure State -->
      <div v-else-if="sessionStatus === 'fail'">
        <div class="desc red" style="margin-top: 16px;">
          {{ $t('Verification failed. Please try again.') }}
        </div>
        <button class="button" style="margin-top: 16px;" @click="startNewVerification">
          <span>{{ $t('Try Again') }}</span>
        </button>
      </div>
    </div>

    <!-- Start Verification -->
    <div v-else>
      <button 
        class="button" 
        style="margin: 32px auto 0; width: 100%; max-width: 596px;"
        @click="startNewVerification" 
        :disabled="isStartingVerification"
      >
        <span v-if="!isStartingVerification">{{ $t('Start Verification') }}</span>
        <span v-else>{{ $t('Starting...') }}</span>
      </button>
    </div>

    <!-- Error Display -->
    <div v-if="verificationError" class="desc red" style="margin-top: 16px;">
      {{ verificationError }}
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'FincheckerVerification',
  
  data() {
    return {
      showSuccessMessage: false,
      successTimeout: null
    }
  },

  computed: {
    ...mapGetters('finchecker', [
      'fincheckerSession',
      'isStartingVerification',
      'showInlineVerification',
      'verificationError',
      'hasActiveSession',
      'isPendingVerification',
      'isVerificationComplete',
      'sessionStatus'
    ])
  },

  methods: {
    ...mapActions('finchecker', [
      'startVerification',
      'reopenVerification',
      'toggleInlineVerification',
      'resetSession',
      'clearError'
    ]),

    async startNewVerification() {
      const result = await this.startVerification()
      
      if (result.success) {
        this.showSuccessMessage = true
        this.autoHideSuccess()
      }
    },

    cancelSession() {
      this.resetSession()
      this.hideSuccessMessage()
    },

    toggleInlineMode() {
      this.toggleInlineVerification()
    },

    hideSuccessMessage() {
      this.showSuccessMessage = false
      if (this.successTimeout) {
        clearTimeout(this.successTimeout)
      }
    },

    autoHideSuccess() {
      if (this.successTimeout) {
        clearTimeout(this.successTimeout)
      }
      this.successTimeout = setTimeout(() => {
        this.hideSuccessMessage()
      }, 5000)
    },

    formatStatus(status) {
      const statusMap = {
        'pending': this.$t('Pending'),
        'success': this.$t('Approved'),
        'fail': this.$t('Failed')
      }
      return statusMap[status] || status
    }
  },

  mounted() {
    // Watch for verification completion
    this.$watch('isVerificationComplete', (completed) => {
      if (completed) {
        this.$emit('verification-complete')
      }
    })
  },

  beforeUnmount() {
    if (this.successTimeout) {
      clearTimeout(this.successTimeout)
    }
  }
}
</script>

<style scoped>
.status-badge {
  display: inline-block;
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.pending {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.status-badge.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.status-badge.fail {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

@media screen and (max-width: 768px) {
  .status-badge {
    font-size: 12px;
    padding: 4px 8px;
  }
}
</style>
